import { OrbitControls } from '@react-three/drei';
import { useFrame, useThree } from '@react-three/fiber';
import {
  EARTH,
  degToRad,
  ecefToEci,
  geodeticToEcef,
  gmst,
  isoToEpochSeconds,
  moonPosition,
} from '@trajectory/astro';
import { useEffect, useMemo, useRef, type ElementRef } from 'react';
import * as THREE from 'three';
import { playbackClock } from '@/lib/playbackClock';
import { useCatalogLaunchSites } from '@/hooks/useCatalog';
import { useMissionStore } from '@/stores/missionStore';
import { useSettingsStore } from '@/stores/settingsStore';
import { useTelemetryStore } from '@/stores/telemetryStore';
import { eciToThree, toScene } from './constants';
import { currentSceneEpoch, useSceneEpochBase } from './playbackEpoch';
import { geocentricSceneBounds, interpolateSceneTelemetryPosition } from './trajectoryCoordinates';

export type CameraFocusMode = 'globe' | 'mission' | 'launch' | 'destination' | 'vehicle';
export type CameraZoomDirection = 'in' | 'out';
export const CAMERA_FOCUS_EVENT = 'trajectory:camera-focus';
export const CAMERA_ZOOM_EVENT = 'trajectory:camera-zoom';

export function focusViewportCamera(mode: CameraFocusMode): void {
  window.dispatchEvent(new CustomEvent<CameraFocusMode>(CAMERA_FOCUS_EVENT, { detail: mode }));
}

export function zoomViewportCamera(direction: CameraZoomDirection): void {
  window.dispatchEvent(
    new CustomEvent<CameraZoomDirection>(CAMERA_ZOOM_EVENT, { detail: direction }),
  );
}

const EARTH_RADIUS = toScene(EARTH.equatorialRadius);
const MIN_CAMERA_DISTANCE = EARTH_RADIUS * 1.08;
const MAX_CAMERA_DISTANCE = EARTH_RADIUS * 160;

function telemetryPoint(time: number): THREE.Vector3 | null {
  const series = useTelemetryStore.getState().series;
  if (!series) return null;
  const position = interpolateSceneTelemetryPosition(series, time);
  return position ? new THREE.Vector3(...position.point) : null;
}

/** Orbit controls plus deterministic globe/launch/vehicle focus modes. */
export function CameraRig() {
  const controlsRef = useRef<ElementRef<typeof OrbitControls>>(null);
  const modeRef = useRef<CameraFocusMode>('globe');
  const previousTargetRef = useRef(new THREE.Vector3());
  const destinationTargetRef = useRef(new THREE.Vector3());
  const destinationPositionRef = useRef(
    new THREE.Vector3(0, EARTH_RADIUS * 2.2, EARTH_RADIUS * 3.2),
  );
  const transitioningRef = useRef(false);
  const camera = useThree((state) => state.camera);
  const invalidate = useThree((state) => state.invalidate);
  const reducedMotion = useSettingsStore((state) => state.reducedMotion);
  const launchSites = useCatalogLaunchSites();
  const launchSiteId = useMissionStore((state) => state.document?.launchSiteId);
  const launchAt = useMissionStore((state) => state.document?.launchAt);
  const baseEpoch = useSceneEpochBase();
  const fallbackLaunchPoint = useMemo(() => {
    const site = launchSites.data?.find((candidate) => candidate.id === launchSiteId);
    if (!site || !launchAt) return null;
    const epoch = isoToEpochSeconds(launchAt);
    const ecef = geodeticToEcef({
      latitude: degToRad(site.latitudeDeg),
      longitude: degToRad(site.longitudeDeg),
      altitude: site.elevationMeters + 45_000,
    });
    return new THREE.Vector3(...eciToThree(ecefToEci(ecef, gmst(epoch))));
  }, [launchAt, launchSiteId, launchSites.data]);

  useEffect(() => {
    const setFocus = (event: Event) => {
      const mode = (event as CustomEvent<CameraFocusMode>).detail;
      modeRef.current = mode;
      const controls = controlsRef.current;
      if (!controls) return;

      let target = new THREE.Vector3();
      if (mode === 'launch') {
        target = telemetryPoint(0) ?? fallbackLaunchPoint ?? target;
      }
      if (mode === 'vehicle') {
        target = telemetryPoint(playbackClock.time) ?? telemetryPoint(0) ?? target;
      }
      if (mode === 'destination') {
        target = new THREE.Vector3(...eciToThree(moonPosition(currentSceneEpoch(baseEpoch))));
      }
      const series = useTelemetryStore.getState().series;
      const missionBounds = mode === 'mission' && series ? geocentricSceneBounds(series) : null;
      if (missionBounds) {
        target = new THREE.Vector3(...missionBounds.center);
      }
      previousTargetRef.current.copy(target);

      const destinationPosition = new THREE.Vector3();
      if (mode === 'globe') {
        destinationPosition.set(0, EARTH_RADIUS * 2.2, EARTH_RADIUS * 3.2);
      } else if (mode === 'mission' && missionBounds) {
        const distance = THREE.MathUtils.clamp(
          Math.max(EARTH_RADIUS * 3.4, missionBounds.radius * 2.35),
          MIN_CAMERA_DISTANCE,
          MAX_CAMERA_DISTANCE,
        );
        destinationPosition
          .copy(target)
          .add(new THREE.Vector3(0.18, 0.58, 1).normalize().multiplyScalar(distance));
      } else {
        const outward = target.clone().normalize();
        if (outward.lengthSq() === 0) outward.set(0, 0.35, 1).normalize();
        destinationPosition.copy(
          target
            .clone()
            .add(outward.multiplyScalar(EARTH_RADIUS * (mode === 'launch' ? 1.45 : 1.15)))
            .add(new THREE.Vector3(0, EARTH_RADIUS * 0.2, 0)),
        );
      }

      destinationTargetRef.current.copy(target);
      destinationPositionRef.current.copy(destinationPosition);
      if (reducedMotion) {
        controls.target.copy(target);
        camera.position.copy(destinationPosition);
        controls.update();
        transitioningRef.current = false;
      } else {
        transitioningRef.current = true;
      }
      invalidate();
    };
    window.addEventListener(CAMERA_FOCUS_EVENT, setFocus);
    return () => window.removeEventListener(CAMERA_FOCUS_EVENT, setFocus);
  }, [baseEpoch, camera, fallbackLaunchPoint, invalidate, reducedMotion]);

  useEffect(() => {
    const zoomCamera = (event: Event) => {
      const controls = controlsRef.current;
      if (!controls) return;

      const direction = (event as CustomEvent<CameraZoomDirection>).detail;
      const offset = camera.position.clone().sub(controls.target);
      if (offset.lengthSq() === 0) offset.set(0, 0, MIN_CAMERA_DISTANCE);

      const distance = THREE.MathUtils.clamp(
        offset.length() * (direction === 'in' ? 0.72 : 1.38),
        MIN_CAMERA_DISTANCE,
        MAX_CAMERA_DISTANCE,
      );
      transitioningRef.current = false;
      camera.position.copy(controls.target).add(offset.setLength(distance));
      controls.update();
      invalidate();
    };

    window.addEventListener(CAMERA_ZOOM_EVENT, zoomCamera);
    return () => window.removeEventListener(CAMERA_ZOOM_EVENT, zoomCamera);
  }, [camera, invalidate]);

  useFrame((_state, deltaSeconds) => {
    const controls = controlsRef.current;
    if (!controls) return;

    if (modeRef.current === 'vehicle' || modeRef.current === 'destination') {
      const nextTarget =
        modeRef.current === 'vehicle'
          ? telemetryPoint(playbackClock.time)
          : new THREE.Vector3(...eciToThree(moonPosition(currentSceneEpoch(baseEpoch))));
      if (nextTarget) {
        const delta = nextTarget.clone().sub(previousTargetRef.current);
        if (transitioningRef.current) {
          destinationPositionRef.current.add(delta);
          destinationTargetRef.current.copy(nextTarget);
        } else {
          camera.position.add(delta);
          controls.target.copy(nextTarget);
        }
        previousTargetRef.current.copy(nextTarget);
      }
    }

    if (transitioningRef.current) {
      const alpha = 1 - Math.exp(-Math.min(deltaSeconds, 0.1) * 7.5);
      camera.position.lerp(destinationPositionRef.current, alpha);
      controls.target.lerp(destinationTargetRef.current, alpha);
      if (
        camera.position.distanceToSquared(destinationPositionRef.current) < 0.0002 &&
        controls.target.distanceToSquared(destinationTargetRef.current) < 0.0002
      ) {
        camera.position.copy(destinationPositionRef.current);
        controls.target.copy(destinationTargetRef.current);
        transitioningRef.current = false;
      }
    }
    controls.update();
    if (transitioningRef.current) invalidate();
  });

  return (
    <OrbitControls
      ref={controlsRef}
      makeDefault
      enableDamping={!reducedMotion}
      dampingFactor={0.08}
      minDistance={MIN_CAMERA_DISTANCE}
      maxDistance={MAX_CAMERA_DISTANCE}
      minPolarAngle={0.025}
      maxPolarAngle={Math.PI - 0.025}
      zoomSpeed={0.9}
      zoomToCursor
      rotateSpeed={0.58}
      panSpeed={0.42}
      screenSpacePanning
      onStart={() => {
        transitioningRef.current = false;
      }}
    />
  );
}
